export type RuleStatus = 'active' | 'scheduled' | 'expired';

export type RuleStatusFilterValue = 'active_scheduled' | 'all' | 'expired';

export interface RuleWindow {
  activeFrom: string | number | Date;
  activeUntil: string | number | Date;
}

// Sentinel end date for a "mute forever" rule (no end). It fits MySQL DATETIME's
// max (9999-12-31 23:59:59), so it persists without a schema change and always
// satisfies the notifier's `active_until > now` filter, keeping the rule active
// indefinitely.
export const FOREVER_UNTIL_ISO = '9999-12-31T23:59:59.000Z';

// A window has no end when its `activeUntil` lands in year 9999+ (the sentinel).
// Year-based so it works regardless of ms precision or how the value round-trips.
export const isForeverUntil = (value: string | number | Date): boolean =>
  new Date(value).getUTCFullYear() >= 9999;

// A rule is `scheduled` before its window opens, `expired` once the window has
// closed, and `active` in between. `now` is injectable for deterministic tests.
export const getRuleStatus = (window: RuleWindow, now: Date = new Date()): RuleStatus => {
  const from = new Date(window.activeFrom);
  const until = new Date(window.activeUntil);
  if (now < from) return 'scheduled';
  if (now >= until) return 'expired';
  return 'active';
};

// The default list view hides expired rules: "Active & Scheduled" is the
// default filter, with explicit "All" and "Expired" alternatives.
export const matchesStatusFilter = (status: RuleStatus, filter: RuleStatusFilterValue): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'expired':
      return status === 'expired';
    case 'active_scheduled':
    default:
      return status === 'active' || status === 'scheduled';
  }
};
